<script>
import {defineComponent, reactive} from "vue";
import {useLandingPageStore} from "../../../stores/landingPage";

export default defineComponent({
  props: {
    title: {
      type: String,
      required: false
    },
    titleStyleClass: {
      type: String,
      default: 'headline-1-regular'
    },
    subtitle: {
      type: String,
      default: ""
    },
    subtitleStyleClass: {
      type: String,
      default: 'title-regular'
    },
    bodyText: {
      type: [String, Array],
      default: ""
    },
    bodyTextStyleClass: {
      type: String,
      default: 'title-medium'
    },
    textMaxWidth: {
      type: String,
      default: "500px"
    },
    textHorizontalPosition: {
      type: String,
      default: "center",
      validator: (value) => ['left', 'center', 'right'].includes(value)
    },
    textVerticalPosition: {
      type: String,
      default: "center",
      validator: (value) => ['top', 'center', 'bottom'].includes(value)
    },
    bannerImage: {
      type: Number,
      required: false
    },
    backgroundImagePosition: {
      type: String,
      default: 'center'
    },
    backgroundImageOverlay: {
      type: String,
      default: "linear-gradient(#00000099, #00000099)"
    },
    titleColor: {
      type: String,
      default: 'rgba(225,225,225,1)'
    },
  },
  setup(props) {
    props = reactive(props);
    const landingStore = useLandingPageStore();
    return { landingStore };
  },
  computed: {
    hasOverlay() {
      return !!(this.title || this.subtitle);
    },
    imageSrc() {
      if (!this.bannerImage) return null;
      return this.landingStore.getResourceById(this.bannerImage)?.resource_url;
    },
    lowResBackgroundImageSrc() {
      if (!this.bannerImage) return null;
      return this.landingStore.getResourceById(this.bannerImage)?.low_resolution_url;
    },
    cssProps() {
      return {
        '--background-image-src': this.imageSrc ? `url('${this.imageSrc}')` : 'none',
        '--background-image-low-res-src': this.lowResBackgroundImageSrc ? `url('${this.lowResBackgroundImageSrc}')` : 'none',
        '--background-image-position': this.backgroundImagePosition,
        '--background-image-overlay': this.hasOverlay ? this.backgroundImageOverlay : 'none',
        '--text-container-justify': this.calculateFlexPosition(this.textHorizontalPosition),
        '--text-container-align': this.calculateFlexPosition(this.textVerticalPosition),
        '--text-align': this.textHorizontalPosition,
        '--text-max-width': this.textMaxWidth,
        '--title-color': this.titleColor
      };
    },
    bodyTextArr() {
      if (typeof this.bodyText === 'string') {
        return [this.bodyText];
      }
      return this.bodyText;
    }
  },
  methods: {
    calculateFlexPosition(position) {
      if (position === "right" || position === "bottom") {
        return 'flex-end';
      } else if (position === "center") {
        return 'center';
      }
      return "flex-start";
    }
  }
});
</script>

<template>
  <div class="outer-container">
    <img
      v-show="false"
      v-if="lowResBackgroundImageSrc"
      :src="lowResBackgroundImageSrc"
      rel="preload"
    >
    <div
      id="landing-hero-banner"
      class="inner-container"
      :style="cssProps"
    >
      <div class="hero-banner-content">
        <div class="text-container">
          <h1 :class="titleStyleClass">
            {{ title }}
          </h1>
          <p
            v-if="subtitle"
            :class="['hero-banner-subtitle', subtitleStyleClass]"
          >
            {{ subtitle }}
          </p>
          <div
            v-if="bodyText"
            :class="['hero-banner-body-text', bodyTextStyleClass]"
          >
            <p
              v-for="(p, i) in bodyTextArr"
              :key="i"
            >
              {{ p }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

#landing-hero-banner {
  position: relative;
  display: block;
  width: 100%;
  max-width: none !important;
  height: 50vh;
  min-height: 400px;
  padding: 0 !important;
  margin: 0 !important;
  background-image: var(--background-image-src), var(--background-image-low-res-src);
  background-size: cover;
  background-position: var(--background-image-position);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: var(--background-image-overlay);
    z-index: 1;
  }

  .hero-banner-content {
    display: flex;
    justify-content: var(--text-container-justify);
    align-items: var(--text-container-align);
    text-align: var(--text-align);
    height: 100%;
    width: 100%;

    .text-container {
      display: flex;
      flex-direction: column;
      color: var(--title-color);
      position: relative;
      z-index: 2;
      max-width: var(--text-max-width);

      .hero-banner-subtitle {
        margin-top: $space-sm;
      }

      .hero-banner-body-text {
        margin-top: $space-xl;
      }
    }
  }
}

@media screen and (max-width: $breakpoint-max-sm) {
  #landing-hero-banner {
    height: 40vh;
    min-height: 300px;
  }
}

@container (min-width: 0px) and (max-width: 479px) {
  #landing-hero-banner {
    height: 40vh !important;
    min-height: 300px !important;
  }
}
</style>
